import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { PaymentTestModeBanner } from "@/components/PaymentTestModeBanner";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { AdminRoute } from "@/components/AdminRoute";
import Index from "./pages/Index.tsx";
import NotFound from "./pages/NotFound.tsx";
import Auth from "./pages/Auth.tsx";
import Dashboard from "./pages/Dashboard.tsx";
import Admin from "./pages/Admin.tsx";
import AdminAuditLogs from "./pages/AdminAuditLogs.tsx";
import AdminSecurityFindings from "./pages/AdminSecurityFindings.tsx";
import AdminSecurityFindingDiff from "./pages/AdminSecurityFindingDiff.tsx";
import AdminSecurityHistory from "./pages/AdminSecurityHistory.tsx";
import AdminSecurityRegister from "./pages/AdminSecurityRegister.tsx";

import CheckoutSuccess from "./pages/CheckoutSuccess.tsx";
import CheckoutPending from "./pages/CheckoutPending.tsx";
import ForgotPassword from "./pages/ForgotPassword.tsx";
import ResetPassword from "./pages/ResetPassword.tsx";
import Onboarding from "./pages/Onboarding.tsx";
import Support from "./pages/Support.tsx";
import SharedBlueprint from "./pages/SharedBlueprint.tsx";
import Terms from "./pages/Terms.tsx";
import Privacy from "./pages/Privacy.tsx";
import Account from "./pages/Account.tsx";
import Marketplace from "./pages/Marketplace.tsx";
import About from "./pages/About.tsx";
import BlueprintProduct from "./pages/BlueprintProduct.tsx";

import Roadmap from "./pages/Roadmap.tsx";
import Milestones from "./pages/Milestones.tsx";
import Launch from "./pages/Launch.tsx";
import LaunchList from "./pages/LaunchList.tsx";

import HelpdeskArchitectureDocs from "./pages/HelpdeskArchitectureDocs.tsx";
import HelpdeskRolloutRunbook from "./pages/HelpdeskRolloutRunbook.tsx";
import HelpdeskWidget from "@/components/support/HelpdeskWidget";
import { useLocation } from "react-router-dom";
import { usePageViews } from "@/hooks/usePageViews";
import { ReferralCapture } from "@/components/ReferralCapture";

const queryClient = new QueryClient();

const RouteTracker = () => {
  usePageViews();
  return null;
};

// Hide the floating launcher on routes that already embed the assistant inline
// (e.g. /support full-page version) and on auth/admin flows where it would distract.
const HIDDEN_ON = ["/support", "/auth", "/forgot-password", "/reset-password", "/admin", "/onboarding"];

const GlobalHelpdeskMount = () => {
  const { pathname } = useLocation();
  if (HIDDEN_ON.some((p) => pathname === p || pathname.startsWith(p + "/"))) return null;
  return <HelpdeskWidget variant="floating" />;
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <RouteTracker />
        <ReferralCapture />
        <PaymentTestModeBanner />
        <GlobalHelpdeskMount />
        <Routes>
          <Route path="/" element={<Index />} />
          <Route path="/auth" element={<Auth />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/checkout/success" element={<CheckoutSuccess />} />
          <Route path="/checkout/pending" element={<CheckoutPending />} />
          <Route path="/support" element={<Support />} />
          <Route path="/b/:slug" element={<SharedBlueprint />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/account" element={<Account />} />
          <Route path="/marketplace" element={<Marketplace />} />
          <Route path="/about" element={<About />} />

          <Route path="/blueprint/:id" element={<BlueprintProduct />} />

          <Route path="/roadmap" element={<Roadmap />} />
          <Route path="/milestones" element={<Milestones />} />
          <Route path="/launch" element={<Launch />} />
          <Route path="/launch-list" element={<LaunchList />} />

          <Route path="/docs/helpdesk-architecture" element={<HelpdeskArchitectureDocs />} />
          <Route
            path="/docs/helpdesk-rollout-runbook"
            element={
              <ProtectedRoute>
                <AdminRoute>
                  <HelpdeskRolloutRunbook />
                </AdminRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path="/onboarding"
            element={
              <ProtectedRoute requireLicense={false}>
                <Onboarding />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin"
            element={
              <ProtectedRoute>
                <AdminRoute>
                  <Admin />
                </AdminRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/audit-logs"
            element={
              <ProtectedRoute>
                <AdminRoute>
                  <AdminAuditLogs />
                </AdminRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/security-findings"
            element={
              <ProtectedRoute>
                <AdminRoute>
                  <AdminSecurityFindings />
                </AdminRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/security-findings/diff/:snapshotId"
            element={
              <ProtectedRoute>
                <AdminRoute>
                  <AdminSecurityFindingDiff />
                </AdminRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/security-history"
            element={
              <ProtectedRoute>
                <AdminRoute>
                  <AdminSecurityHistory />
                </AdminRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/security-register"
            element={
              <ProtectedRoute>
                <AdminRoute>
                  <AdminSecurityRegister />
                </AdminRoute>
              </ProtectedRoute>
            }
          />
          {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}

          <Route path="*" element={<NotFound />} />
        </Routes>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
